<div [class]="shellClasses">

  <header class="mb-6 sm:mb-8">
    <h1 class="tfr-page-heading">Gestión reservas</h1>
    <p class="tfr-page-lead">
      Cada pestaña agrupa un tipo de tarea: pagos, fianza o cierre del evento.
    </p>
  </header>

  <!-- Filter bar -->
  <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6
              rounded-2xl border border-pale-gray bg-snow-white px-4 py-3.5 shadow-tfr-sm">
    <div class="flex flex-col gap-2 min-w-0 sm:flex-row sm:items-center sm:gap-4">
      <label class="text-[11px] font-semibold uppercase tracking-wide text-steel-gray shrink-0">
        Local
      </label>
      <select
        class="tfr-select-sm w-full sm:w-auto sm:min-w-[200px] max-w-full bg-cloud-mist text-midnight-indigo"
        [value]="selectedSpaceId()"
        (change)="onSpaceFilterChange($event)">
        @for (opt of spaceOptions(); track opt.id) {
          <option [value]="opt.id">{{ opt.label }}</option>
        }
      </select>
    </div>
    <p class="text-[12px] font-medium tabular-nums text-midnight-indigo shrink-0 sm:text-right">
      {{ actionsRequiredLabel() }}
    </p>
  </div>

  <nav
    class="flex gap-0.5 overflow-x-auto border-b border-pale-gray mb-3 -mx-0.5 px-0.5
           [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    aria-label="Tareas de gestión">
    @for (tab of tabs; track tab.id) {
      <button
        type="button"
        (click)="setTab(tab.id)"
        class="relative shrink-0 px-3 sm:px-4 py-2.5 text-[13px] font-semibold transition-colors whitespace-nowrap"
        [class.text-midnight-indigo]="activeTab() === tab.id"
        [class.text-steel-gray]="activeTab() !== tab.id"
        [class.hover:text-slate-blue]="activeTab() !== tab.id"
        [attr.aria-current]="activeTab() === tab.id ? 'page' : null">
        <span class="inline-flex items-center gap-2">
          {{ tab.label }}
          @if (tabCounts()[tab.id] > 0) {
            <span
              class="inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full px-1.5
                     text-[10px] font-bold tabular-nums bg-midnight-indigo text-snow-white">
              {{ tabCounts()[tab.id] }}
            </span>
          }
        </span>
        @if (activeTab() === tab.id) {
          <span class="absolute left-0 right-0 -bottom-px h-0.5 bg-midnight-indigo rounded-full" aria-hidden="true"></span>
        }
      </button>
    }
  </nav>
  <p class="text-[12px] text-steel-gray mb-6">{{ activeTabHint() }}</p>

  @if (loading()) {
    <div class="rounded-2xl border border-pale-gray bg-snow-white p-10 text-center">
      <lucide-icon name="loader-circle" [size]="22" class="animate-spin mx-auto text-steel-gray" />
      <p class="mt-3 text-[13px] text-steel-gray">Cargando reservas&hellip;</p>
    </div>
  } @else if (loadError()) {
    <div class="rounded-2xl border border-pale-gray bg-cloud-mist p-6 text-center">
      <p class="text-[14px] font-semibold text-midnight-indigo">No se pudieron cargar las reservas</p>
      <p class="mt-1 text-[12px] text-steel-gray">Comprueba tu conexión e inténtalo de nuevo.</p>
    </div>
  } @else if (visibleBookings().length === 0) {
    <div class="rounded-2xl border border-dashed border-platinum-tint bg-cloud-mist/60 p-10 text-center">
      <lucide-icon name="inbox" [size]="28" class="mx-auto text-steel-gray" />
      <p class="mt-3 text-[14px] font-semibold text-midnight-indigo">
        @if (actionsRequiredCount() === 0) {
          Todo al día
        } @else {
          Sin reservas en esta pestaña
        }
      </p>
      <p class="mt-1 text-[12px] text-steel-gray">
        @if (actionsRequiredCount() === 0) {
          No tienes tareas pendientes en ninguna pestaña.
        } @else {
          Revisa las otras pestañas con el contador en la barra superior.
        }
      </p>
    </div>
  } @else {
    @if (actionError()) {
      <p class="mb-4 text-[12px] text-red-600 text-center">{{ actionError() }}</p>
    }

    <ul class="flex flex-col gap-3" role="list">
      @for (booking of visibleBookings(); track booking.id) {
        <li
          class="rounded-2xl border border-pale-gray bg-snow-white p-4 sm:p-5 shadow-tfr-sm
                 transition-shadow hover:shadow-md">

          <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">

            <div class="min-w-0 flex-1">
              <p class="text-[14px] font-bold text-midnight-indigo truncate">
                {{ spaceLabel(booking) }}
              </p>
              <p class="mt-1.5 text-[12px] sm:text-[13px] leading-relaxed text-midnight-indigo sm:leading-snug">
                <span class="sm:hidden flex flex-col gap-0.5">
                  <span class="font-medium">{{ formatDate(booking.date) }}</span>
                  <span class="tabular-nums text-slate-blue">{{ shiftLabel(booking) }}</span>
                  <span>{{ guestLabel(booking) }}</span>
                  @if (booking.totalPrice !== null) {
                    <span class="font-semibold tabular-nums">
                      Total {{ rentalTotalEuros(booking) | number:'1.2-2' }}&nbsp;€
                      @if (showOwnerSignal(booking)) {
                        <span class="font-medium text-slate-blue">
                          &middot; Se&ntilde;al {{ ownerSignalEuros(booking) | number:'1.2-2' }}&nbsp;€
                        </span>
                      } @else if (isStripeFullPayment(booking)) {
                        <span class="font-normal text-steel-gray"> &middot; Cobro online (Stripe)</span>
                      }
                    </span>
                  }
                  @if (activeTab() === 'active' && depositMethodLabel(booking); as methodLabel) {
                    <span class="text-steel-gray">Fianza depositada: <span class="font-medium text-midnight-indigo">{{ methodLabel }}</span></span>
                  }
                </span>
                <span class="hidden sm:inline">
                  <span class="font-medium">{{ formatDate(booking.date) }}</span>
                  <span class="text-steel-gray mx-1.5" aria-hidden="true">·</span>
                  <span class="tabular-nums text-slate-blue">{{ shiftLabel(booking) }}</span>
                  <span class="text-steel-gray mx-1.5" aria-hidden="true">·</span>
                  <span>{{ guestLabel(booking) }}</span>
                  @if (booking.totalPrice !== null) {
                    <span class="text-steel-gray mx-1.5" aria-hidden="true">·</span>
                    <span class="font-semibold tabular-nums">
                      Total {{ rentalTotalEuros(booking) | number:'1.2-2' }}&nbsp;€
                      @if (showOwnerSignal(booking)) {
                        <span class="text-steel-gray mx-1.5" aria-hidden="true">·</span>
                        <span class="font-medium text-slate-blue">
                          Se&ntilde;al {{ ownerSignalEuros(booking) | number:'1.2-2' }}&nbsp;€
                        </span>
                      } @else if (isStripeFullPayment(booking)) {
                        <span class="text-steel-gray mx-1.5" aria-hidden="true">·</span>
                        <span class="font-normal text-steel-gray">Cobro online (Stripe)</span>
                      }
                    </span>
                  }
                  @if (activeTab() === 'active' && depositMethodLabel(booking); as methodLabel) {
                    <span class="text-steel-gray mx-1.5" aria-hidden="true">·</span>
                    <span class="text-steel-gray">Fianza depositada: <span class="font-medium text-midnight-indigo">{{ methodLabel }}</span></span>
                  }
                </span>
              </p>
            </div>

            <div class="flex flex-col gap-2 w-full sm:flex-row sm:flex-wrap sm:w-auto lg:justify-end shrink-0">

              @if (activeTab() === 'pending') {
                <span class="inline-flex items-center gap-1.5 rounded-xl border border-pale-gray
                             bg-cloud-mist px-3 py-2 text-[11px] font-medium text-steel-gray
                             self-center">
                  <lucide-icon name="clock" [size]="13" class="shrink-0" />
                  Esperando firma del contrato
                </span>
              }

              @if (activeTab() === 'verify') {
                @if (booking.fastTrackBizum) {
                  <span class="inline-flex items-center gap-1.5 rounded-xl border border-amber-300
                               bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-900
                               self-center">
                    <lucide-icon name="zap" [size]="13" class="shrink-0" />
                    Bizum urgente — sin justificante
                  </span>
                  <button
                    type="button"
                    (click)="confirmBooking(booking)"
                    [disabled]="actionBusyId() === booking.id"
                    class="w-full sm:w-auto rounded-xl bg-midnight-indigo px-4 py-2.5 text-[12px] font-semibold text-snow-white
                           hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                    @if (actionBusyId() === booking.id) {
                      Validando&hellip;
                    } @else {
                      Confirmar señal Bizum
                    }
                  </button>
                } @else if (booking.depositReceiptUrl) {
                  <button
                    type="button"
                    (click)="openReceipt(booking.depositReceiptUrl)"
                    class="inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-xl border border-pale-gray bg-cloud-mist
                           px-3 py-2.5 text-[12px] font-semibold text-slate-blue
                           hover:bg-pale-gray hover:text-midnight-indigo transition-colors">
                    <lucide-icon name="paperclip" [size]="14" />
                    Ver Justificante
                  </button>
                  <button
                    type="button"
                    (click)="openRejectModal(booking)"
                    [disabled]="actionBusyId() === booking.id"
                    class="w-full sm:w-auto rounded-xl border border-platinum-tint bg-snow-white px-3.5 py-2.5
                           text-[12px] font-semibold text-steel-gray
                           hover:bg-cloud-mist hover:text-midnight-indigo
                           disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    Rechazar justificante
                  </button>
                  <button
                    type="button"
                    (click)="confirmBooking(booking)"
                    [disabled]="actionBusyId() === booking.id"
                    class="w-full sm:w-auto rounded-xl bg-midnight-indigo px-4 py-2.5 text-[12px] font-semibold text-snow-white
                           hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                    @if (actionBusyId() === booking.id) {
                      Validando&hellip;
                    } @else {
                      Validar y Confirmar
                    }
                  </button>
                } @else {
                  <span class="text-[11px] text-steel-gray italic text-center sm:text-left">Sin justificante adjunto</span>
                }
              }

              @if (activeTab() === 'confirmed') {
                @if (needsDepositReceiptReview(booking)) {
                  <button
                    type="button"
                    (click)="openSecurityDepositReceipt(booking.securityDepositReceiptUrl)"
                    class="inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-xl border border-pale-gray bg-cloud-mist
                           px-3 py-2.5 text-[12px] font-semibold text-slate-blue hover:bg-pale-gray transition-colors">
                    <lucide-icon name="paperclip" [size]="14" />
                    Ver fianza
                  </button>
                  <button
                    type="button"
                    (click)="confirmSubmittedDeposit(booking)"
                    [disabled]="actionBusyId() === booking.id"
                    class="w-full sm:w-auto rounded-xl bg-midnight-indigo px-4 py-2.5 text-[12px] font-semibold text-snow-white
                           hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                    Validar fianza
                  </button>
                } @else if (hasSecurityDepositReceived(booking) && depositMethodLabel(booking); as methodLabel) {
                  <span class="text-[11px] text-steel-gray sm:self-center">
                    Fianza: {{ methodLabel }}
                  </span>
                } @else if (pendingCashDeposit(booking)) {
                  <span class="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1 sm:self-center">
                    Cliente pagará fianza en efectivo
                  </span>
                }
                @if (!needsDepositReceiptReview(booking)) {
                <button
                  type="button"
                  (click)="openDepositMethodModal(booking)"
                  [disabled]="actionBusyId() === booking.id || hasSecurityDepositReceived(booking) || needsDepositReceiptReview(booking)"
                  class="w-full sm:w-auto rounded-xl border border-pale-gray bg-cloud-mist px-4 py-2.5
                         text-[12px] font-semibold text-midnight-indigo
                         hover:bg-pale-gray disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  @if (hasSecurityDepositReceived(booking)) {
                    Fianza recibida
                  } @else if (pendingCashDeposit(booking)) {
                    Confirmar fianza (efectivo)
                  } @else {
                    Fianza recibida
                  }
                </button>
                }
                <button
                  type="button"
                  (click)="activateBooking(booking)"
                  [disabled]="actionBusyId() === booking.id"
                  class="w-full sm:w-auto rounded-xl bg-midnight-indigo px-4 py-2.5 text-[12px] font-semibold text-snow-white
                         hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  @if (actionBusyId() === booking.id) {
                    Activando&hellip;
                  } @else if (canActivateNormally(booking)) {
                    Activar
                  } @else {
                    Activar sin fianza
                  }
                </button>
              }

              @if (activeTab() === 'active') {
                @if (depositSkippedAtActivation(booking)) {
                  <span class="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1 sm:self-center">
                    Activada sin fianza en plataforma
                  </span>
                  <button
                    type="button"
                    (click)="openCompleteEventModal(booking)"
                    [disabled]="actionBusyId() === booking.id"
                    class="w-full sm:w-auto rounded-xl bg-midnight-indigo px-4 py-2.5 text-[12px] font-semibold text-snow-white
                           hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                    @if (actionBusyId() === booking.id) {
                      Guardando&hellip;
                    } @else {
                      Finalizar evento
                    }
                  </button>
                } @else {
                  @if (booking.securityDepositMethod === 'card_hold' && booking.securityDepositStatus === 'confirmed') {
                    <button
                      type="button"
                      (click)="openCaptureDepositModal(booking)"
                      [disabled]="actionBusyId() === booking.id"
                      class="w-full sm:w-auto rounded-xl bg-action-blue px-4 py-2.5 text-[12px] font-semibold text-snow-white
                             hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                      @if (actionBusyId() === booking.id) {
                        Procesando&hellip;
                      } @else {
                        Cobrar fianza
                      }
                    </button>
                  }
                  <button
                    type="button"
                    (click)="openReturnDepositModal(booking)"
                    [disabled]="actionBusyId() === booking.id"
                    class="w-full sm:w-auto rounded-xl bg-midnight-indigo px-4 py-2.5 text-[12px] font-semibold text-snow-white
                           hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                    @if (actionBusyId() === booking.id) {
                      Guardando&hellip;
                    } @else if (booking.securityDepositMethod === 'card_hold') {
                      Liberar retención
                    } @else {
                      Devolver fianza
                    }
                  </button>
                }
              }

            </div>
          </div>
        </li>
      }
    </ul>
  }
</div>

@if (confirmModal(); as confirmState) {
  @if (confirmModalCopy(); as copy) {
    <div
      class="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-midnight-indigo/40 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      (click)="closeConfirmModal()"
      (keydown.escape)="closeConfirmModal()">
      <div
        class="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-pale-gray bg-snow-white shadow-tfr-md overflow-hidden"
        (click)="$event.stopPropagation()">
        <header class="flex items-center justify-between px-4 py-3 border-b border-pale-gray bg-cloud-mist">
          <div class="min-w-0 pr-3">
            <p id="confirm-modal-title" class="text-[13px] font-semibold text-midnight-indigo">
              {{ copy.title }}
            </p>
            <p class="text-[11px] text-steel-gray truncate mt-0.5">
              {{ spaceLabel(confirmState.booking) }} &middot; {{ guestLabel(confirmState.booking) }}
            </p>
          </div>
          <button
            type="button"
            (click)="closeConfirmModal()"
            [disabled]="actionBusyId() === confirmState.booking.id"
            class="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center text-steel-gray hover:bg-pale-gray transition-colors disabled:opacity-40"
            aria-label="Cerrar">
            <lucide-icon name="x" [size]="18" />
          </button>
        </header>
        <div class="p-4">
          <p class="text-[13px] leading-relaxed text-slate-blue">
            {{ copy.body }}
          </p>
          @if (confirmState.kind === 'captureDeposit') {
            <label class="mt-4 block">
              <span class="text-[11px] font-semibold uppercase tracking-wider text-steel-gray">
                Importe a cobrar (€)
              </span>
              <div class="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  inputmode="decimal"
                  min="0.50"
                  [max]="(confirmState.booking.depositCents ?? 0) / 100"
                  step="0.01"
                  [value]="captureAmountEur()"
                  (input)="onCaptureAmountInput($event)"
                  [disabled]="actionBusyId() === confirmState.booking.id"
                  placeholder="0.00"
                  class="w-32 rounded-xl border border-pale-gray bg-cloud-mist px-3 py-2.5
                         text-[13px] text-midnight-indigo font-mono
                         focus:border-slate-blue focus:outline-none focus:ring-2 focus:ring-slate-blue/20
                         disabled:opacity-50" />
                <span class="text-[12px] text-steel-gray">
                  máx. {{ (confirmState.booking.depositCents ?? 0) / 100 | number:'1.2-2' }} €
                </span>
              </div>
              @if (captureAmountEur() && !captureAmountValid()) {
                <p class="mt-1 text-[11px] text-red-600">
                  Introduce un importe entre 0,50 € y {{ (confirmState.booking.depositCents ?? 0) / 100 | number:'1.2-2' }} €.
                </p>
              }
            </label>
          }
          @if (confirmState.kind === 'reject') {
            <label class="mt-4 block">
              <span class="text-[11px] font-semibold uppercase tracking-wider text-steel-gray">
                Motivo para el cliente <span class="font-normal normal-case tracking-normal text-steel-gray">(opcional)</span>
              </span>
              <textarea
                rows="3"
                [value]="rejectNote()"
                (input)="onRejectNoteInput($event)"
                [disabled]="actionBusyId() === confirmState.booking.id"
                placeholder="Ej.: El importe no coincide, el titular es incorrecto o el documento no es legible."
                class="mt-2 w-full rounded-xl border border-pale-gray bg-cloud-mist px-3 py-2.5
                       text-[13px] text-midnight-indigo placeholder:text-steel-gray/70
                       focus:border-slate-blue focus:outline-none focus:ring-2 focus:ring-slate-blue/20
                       disabled:opacity-50 resize-y min-h-[4.5rem]"></textarea>
              <span class="mt-1 block text-[10px] text-steel-gray text-right tabular-nums">
                {{ rejectNote().length }}/{{ rejectNoteMaxLength }}
              </span>
            </label>
          }
          @if (actionError()) {
            <p class="mt-3 text-[12px] text-red-600">{{ actionError() }}</p>
          }
          <div class="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              (click)="closeConfirmModal()"
              [disabled]="actionBusyId() === confirmState.booking.id"
              class="w-full sm:w-auto rounded-xl border border-platinum-tint bg-snow-white px-4 py-2.5
                     text-[12px] font-semibold text-steel-gray hover:bg-cloud-mist transition-colors
                     disabled:opacity-40 disabled:cursor-not-allowed">
              Cancelar
            </button>
            <button
              type="button"
              (click)="executeConfirmModal()"
              [disabled]="actionBusyId() === confirmState.booking.id || !captureAmountValid()"
              class="w-full sm:w-auto rounded-xl px-4 py-2.5 text-[12px] font-semibold text-snow-white
                     hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              [class.bg-midnight-indigo]="!copy.destructive"
              [class.bg-red-700]="copy.destructive"
              [class.hover:brightness-110]="!copy.destructive"
              [class.hover:bg-red-800]="copy.destructive">
              @if (actionBusyId() === confirmState.booking.id) {
                Procesando&hellip;
              } @else {
                {{ copy.confirmLabel }}
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  }
}

@if (depositModalBooking(); as modalBooking) {
  <div
    class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-midnight-indigo/40 backdrop-blur-[2px]"
    role="dialog"
    aria-modal="true"
    aria-labelledby="deposit-method-title"
    (click)="closeDepositMethodModal()"
    (keydown.escape)="closeDepositMethodModal()">
    <div
      class="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-pale-gray bg-snow-white shadow-tfr-md overflow-hidden"
      (click)="$event.stopPropagation()">
      <header class="flex items-center justify-between px-4 py-3 border-b border-pale-gray bg-cloud-mist">
        <div class="min-w-0 pr-3">
          <p id="deposit-method-title" class="text-[13px] font-semibold text-midnight-indigo">
            Fianza recibida
          </p>
          <p class="text-[11px] text-steel-gray truncate mt-0.5">
            {{ spaceLabel(modalBooking) }} &middot; {{ guestLabel(modalBooking) }}
          </p>
        </div>
        <button
          type="button"
          (click)="closeDepositMethodModal()"
          [disabled]="actionBusyId() === modalBooking.id"
          class="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center text-steel-gray hover:bg-pale-gray transition-colors disabled:opacity-40"
          aria-label="Cerrar">
          <lucide-icon name="x" [size]="18" />
        </button>
      </header>
      <div class="p-4">
        <p class="text-[12px] text-steel-gray mb-4">
          &iquest;C&oacute;mo has recibido la fianza del cliente?
        </p>
        <div class="flex flex-col gap-2" role="radiogroup" aria-label="Forma de cobro de la fianza">
          @for (opt of depositMethodOptions; track opt.id) {
            <button
              type="button"
              role="radio"
              [attr.aria-checked]="depositMethodSelection() === opt.id"
              (click)="selectDepositMethod(opt.id)"
              class="w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-[13px] font-semibold transition-colors"
              [class.border-midnight-indigo]="depositMethodSelection() === opt.id"
              [class.bg-cloud-mist]="depositMethodSelection() === opt.id"
              [class.text-midnight-indigo]="depositMethodSelection() === opt.id"
              [class.border-pale-gray]="depositMethodSelection() !== opt.id"
              [class.bg-snow-white]="depositMethodSelection() !== opt.id"
              [class.text-slate-blue]="depositMethodSelection() !== opt.id"
              [class.hover:bg-cloud-mist]="depositMethodSelection() !== opt.id">
              <span
                class="h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center"
                [class.border-midnight-indigo]="depositMethodSelection() === opt.id"
                [class.border-steel-gray]="depositMethodSelection() !== opt.id">
                @if (depositMethodSelection() === opt.id) {
                  <span class="h-2 w-2 rounded-full bg-midnight-indigo" aria-hidden="true"></span>
                }
              </span>
              {{ opt.label }}
            </button>
          }
        </div>
        <div class="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            (click)="closeDepositMethodModal()"
            [disabled]="actionBusyId() === modalBooking.id"
            class="w-full sm:w-auto rounded-xl border border-platinum-tint bg-snow-white px-4 py-2.5
                   text-[12px] font-semibold text-steel-gray hover:bg-cloud-mist transition-colors
                   disabled:opacity-40 disabled:cursor-not-allowed">
            Cancelar
          </button>
          <button
            type="button"
            (click)="confirmDepositMethod()"
            [disabled]="!depositMethodSelection() || actionBusyId() === modalBooking.id"
            class="w-full sm:w-auto rounded-xl bg-midnight-indigo px-4 py-2.5 text-[12px] font-semibold text-snow-white
                   hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            @if (actionBusyId() === modalBooking.id) {
              Guardando&hellip;
            } @else {
              Confirmar
            }
          </button>
        </div>
      </div>
    </div>
  </div>
}

@if (previewUrl(); as url) {
  <div
    class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-midnight-indigo/40 backdrop-blur-[2px]"
    role="dialog"
    aria-modal="true"
    aria-label="Vista previa del justificante"
    (click)="closeReceiptPreview()"
    (keydown.escape)="closeReceiptPreview()">
    <div
      class="relative w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl border border-pale-gray bg-snow-white shadow-tfr-md overflow-hidden"
      (click)="$event.stopPropagation()">
      <header class="flex items-center justify-between px-4 py-3 border-b border-pale-gray bg-cloud-mist">
        <p class="text-[13px] font-semibold text-midnight-indigo">Justificante de transferencia</p>
        <button
          type="button"
          (click)="closeReceiptPreview()"
          class="h-8 w-8 rounded-lg flex items-center justify-center text-steel-gray hover:bg-pale-gray transition-colors"
          aria-label="Cerrar">
          <lucide-icon name="x" [size]="18" />
        </button>
      </header>
      <div class="p-3 bg-snow-white overflow-auto max-h-[calc(92vh-3.5rem)] sm:max-h-[calc(90vh-3.5rem)] flex items-center justify-center">
        @if (previewIsPdf() && previewSafeUrl(); as safeUrl) {
          <iframe
            [src]="safeUrl"
            title="Justificante PDF"
            class="w-full min-h-[60vh] sm:min-h-[70vh] rounded-lg border border-pale-gray bg-cloud-mist">
          </iframe>
        } @else {
          <img
            [src]="url"
            alt="Justificante de pago"
            class="max-w-full max-h-[75vh] object-contain rounded-lg border border-pale-gray" />
        }
      </div>
    </div>
  </div>
}
